import * as XLSX from 'xlsx';
import type { FleetOptimisationInputs, FleetOptimisationResult } from '../../../../services/fleetOptimisationCalculator';

function asCurrency(value: number): string {
  return value.toFixed(2);
}

type CostBasis = 'daily' | 'monthly' | 'annual';
type CurrencyCode = 'GBP' | 'EUR' | 'USD' | 'AUD' | 'AED' | 'BRL';
type VisualComparisonExportRow = {
  simulation: string;
  cost: number;
  co2: number;
};
type MetricsComparisonExportRow = {
  simulation: string;
  trips: string | number;
  successRate: string | number;
  utilization: string | number;
  vehicleHours: string | number;
  medianWaitMin: string | number;
  fleetSize: string | number;
  cost: number;
  co2: number;
};

interface ExportOptions {
  includeCurrentOperations: boolean;
  costBasis: CostBasis;
  costMultiplier: number;
  currencyCode?: CurrencyCode;
  visualComparisonRows?: VisualComparisonExportRow[];
  metricsComparisonRows?: MetricsComparisonExportRow[];
}

export function downloadCostsAndStatisticsXlsx(
  inputs: FleetOptimisationInputs,
  results: FleetOptimisationResult,
  runId?: string | null,
  options?: ExportOptions
): void {
  const includeCurrentOperations = options?.includeCurrentOperations ?? true;
  const costBasis = options?.costBasis ?? 'daily';
  const costMultiplier = options?.costMultiplier ?? 1;
  const currencyCode = options?.currencyCode ?? 'GBP';
  const visualComparisonRows = options?.visualComparisonRows ?? [];
  const metricsComparisonRows = options?.metricsComparisonRows ?? [];
  const scaledOptimizedCost = (results.optimizedCost / 250) * costMultiplier;
  const scaledBaselineCost = (results.baselineCost / 250) * costMultiplier;
  const scaledSavings = (results.financialSavings / 250) * costMultiplier;

  const workbook = XLSX.utils.book_new();
  const inputsRows = [
    { Field: 'Current Operations Included', Value: includeCurrentOperations ? 'Yes' : 'No' },
    { Field: 'Currency', Value: currencyCode },
    { Field: 'Cost Basis', Value: `${costBasis} (${costMultiplier}x)` },
    { Field: 'Current Operations Fleet Size', Value: includeCurrentOperations ? inputs.currentFleet : 'N/A' },
    { Field: 'Simulated Fleet Size', Value: inputs.optimizedFleet },
    { Field: 'Current Operations Daily KM', Value: includeCurrentOperations ? inputs.baselineDailyKm : 'N/A' },
    { Field: 'Simulated Fleet Daily KM', Value: inputs.optimizedDailyKm },
    { Field: 'Cost per KM', Value: inputs.costPerKm },
    { Field: 'Driver Cost per Hour', Value: inputs.driverCostPerHour },
    {
      Field: 'Driver Hours Enabled',
      Value: (inputs.baselineDriverHoursEnabled || inputs.optimizedDriverHoursEnabled) ? 'Yes' : 'No',
    },
    {
      Field: 'Current Operations Driver Hours per Vehicle (Daily)',
      Value: inputs.baselineDriverHoursEnabled ? inputs.baselineDriverHoursPerVehicle : 'N/A',
    },
    {
      Field: 'Simulated Fleet Driver Hours per Vehicle (Daily)',
      Value: inputs.optimizedDriverHoursEnabled ? inputs.optimizedDriverHoursPerVehicle : 'N/A',
    },
    { Field: 'Annual Fixed Cost per Vehicle', Value: inputs.annualFixedCost },
    { Field: 'Current Operations CO2 Grams per KM', Value: inputs.baselineCo2GramsPerKm },
    { Field: 'Simulated Fleet CO2 Grams per KM', Value: inputs.optimizedCo2GramsPerKm },
    { Field: 'Analysis Years', Value: inputs.analysisYears },
  ];
  const resultsRows = [
    { Metric: `Simulated Fleet ${costBasis} Cost`, Value: asCurrency(scaledOptimizedCost) },
    { Metric: `Current Operations ${costBasis} Cost`, Value: includeCurrentOperations ? asCurrency(scaledBaselineCost) : 'N/A' },
    { Metric: 'Current Operations Total Cost', Value: asCurrency(results.totalBaselineCost) },
    { Metric: 'Simulated Fleet Total Cost', Value: asCurrency(results.totalOptimizedCost) },
    { Metric: `${costBasis} Savings`, Value: includeCurrentOperations ? asCurrency(scaledSavings) : 'N/A' },
    { Metric: 'Current Operations Annual Driver Cost', Value: asCurrency(results.baselineDriverCost) },
    { Metric: 'Simulated Fleet Annual Driver Cost', Value: asCurrency(results.optimizedDriverCost) },
    { Metric: 'Total Savings', Value: includeCurrentOperations ? asCurrency(results.totalFinancialSavings) : 'N/A' },
    { Metric: 'Annual CO2 Reduction (tonnes)', Value: includeCurrentOperations ? results.co2ReductionTonnes.toFixed(2) : 'N/A' },
    { Metric: 'Total CO2 Reduction (tonnes)', Value: includeCurrentOperations ? results.totalCo2ReductionTonnes.toFixed(2) : 'N/A' },
  ];

  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(inputsRows), 'Inputs');
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(resultsRows), 'Results');
  if (visualComparisonRows.length > 0) {
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(
        visualComparisonRows.map((row) => ({
          Simulation: row.simulation,
          [`${costBasis} Cost (${currencyCode})`]: asCurrency(row.cost),
          'Annual CO2 (tonnes)': row.co2.toFixed(2),
        }))
      ),
      'Visual Comparison'
    );
  }
  if (metricsComparisonRows.length > 0) {
    XLSX.utils.book_append_sheet(
      workbook,
      XLSX.utils.json_to_sheet(
        metricsComparisonRows.map((row) => ({
          Simulation: row.simulation,
          Trips: row.trips,
          'Success Rate': row.successRate,
          Utilization: row.utilization,
          'Vehicle Hours': row.vehicleHours,
          'Median Wait (min)': row.medianWaitMin,
          'Fleet Size': row.fleetSize,
          [`${costBasis} Cost (${currencyCode})`]: asCurrency(row.cost),
          'Annual CO2 (tonnes)': row.co2.toFixed(2),
        }))
      ),
      'Metrics Comparison'
    );
  }

  const suffix = runId ? `_${runId}` : '';
  XLSX.writeFile(workbook, `simulation_costs_and_statistics${suffix}.xlsx`);
}

export const downloadSimulationCostCalculatorXlsx = downloadCostsAndStatisticsXlsx;
